import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Alert, Table } from 'react-bootstrap';
import { askInventoryQuestion } from '../api/aiAssistantAPI';
import InventoryList from '../components/InventoryList';
import '../styles/aiAssistant.css';

const DataTable = ({ columns, rows }) => {
  if (!columns || !rows || rows.length === 0) return null;
  return (
    <div className="ai-data-table-wrapper mt-2">
      <Table striped bordered hover size="sm" className="ai-data-table mb-0">
        <thead>
          <tr>
            {columns.map((col, i) => (
              <th key={i}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j}>{cell != null ? String(cell) : ''}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const ValueCard = ({ label, value }) => (
  <div className="ai-value-card mt-2">
    <span className="ai-value-label">{label}: </span>
    <span className="ai-value-number">{value != null ? String(value) : ''}</span>
  </div>
);

const DataList = ({ items }) => {
  if (!items || items.length === 0) return null;
  return (
    <ul className="ai-data-list mt-2 mb-0">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
};

const StructuredData = ({ data }) => {
  if (!data) return null;
  if (data.type === 'table') return <DataTable columns={data.columns} rows={data.rows} />;
  if (data.type === 'value') return <ValueCard label={data.label} value={data.value} />;
  if (data.type === 'list') return <DataList items={data.items} />;
  return null;
};

const Message = ({ type, content, data }) => (
  <div
    className={`d-flex ${type === 'user' ? 'justify-content-end' : 'justify-content-start'} mb-3`}
  >
    <div
      className={`p-3 rounded-4 ${
        type === 'user'
          ? 'bg-primary text-white rounded-bottom-end-0'
          : 'message-content-container bg-body-tertiary rounded-bottom-start-0'
      }`}
      style={{ maxWidth: type === 'assistant' && data?.type === 'table' ? '90%' : '70%' }}
    >
      <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{content}</p>
      {type === 'assistant' && <StructuredData data={data} />}
    </div>
  </div>
);

const AIAssistantScreen = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [inventoryItemIds, setInventoryItemIds] = useState([]);

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = input.trim();
    setInput('');
    setError(null);
    setInventoryItemIds([]);

    const history = [];
    for (let i = 0; i < messages.length; i++) {
      if (messages[i].type === 'user' && i + 1 < messages.length && messages[i + 1].type === 'assistant') {
        history.push({
          question: messages[i].content,
          answer: messages[i + 1].content,
        });
      }
    }

    setMessages(prev => [...prev, { type: 'user', content: userMessage }]);

    try {
      setLoading(true);
      const response = await askInventoryQuestion({ question: userMessage, history });

      setMessages(prev => [...prev, {
        type: 'assistant',
        content: response.answer,
        data: response.data || null,
      }]);

      if (response.inventory_item_ids && response.inventory_item_ids.length > 0) {
        setInventoryItemIds(response.inventory_item_ids);
      }
    } catch (err) {
      console.error('Error in handleSend:', err);
      setError(err.message || 'Failed to get response');
      setMessages(prev => [...prev, {
        type: 'assistant',
        content: 'Sorry, I encountered an error processing your request.',
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid>
      <Row className="justify-content-center">
        <Col md={11} lg={10}>
          <h1>Inventory AI Assistant</h1>
          <div className="card shadow theme-card" style={{
            height: inventoryItemIds.length > 0 ? 'calc(85vh - 2rem)' : 'calc(100vh - 2rem)',
            maxHeight: '1200px'
          }}>
            <div className="card-body overflow-auto" style={{
              height: 'calc(100% - 180px)'
            }}>
              {messages.map((message, index) => (
                <Message
                  key={index}
                  type={message.type}
                  content={message.content}
                  data={message.data}
                />
              ))}
              <div ref={messagesEndRef} />

              {loading && (
                <div className="d-flex justify-content-center mt-3">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}

              {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                  {error}
                </Alert>
              )}
            </div>

            <div className="card-footer theme-card-footer">
              <Form onSubmit={handleSend}>
                <Form.Control
                  as="textarea"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (input.trim() && !loading) {
                        handleSend(e);
                      }
                    }
                  }}
                  placeholder="Ask a question about inventory..."
                  disabled={loading}
                  rows="3"
                  className="mb-2 theme-input"
                  style={{ resize: 'none', borderRadius: '4px' }}
                  autoComplete="off"
                  spellCheck="true"
                />
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    Press <kbd>Enter</kbd> to send, <kbd>Shift+Enter</kbd> for a new line
                  </small>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={!input.trim() || loading}
                    className="d-flex align-items-center justify-content-center gap-2"
                    style={{ height: '38px', width: '100px', minWidth: '100px' }}
                  >
                    <span>Send</span>
                    <i className="material-icons">send</i>
                  </Button>
                </div>
              </Form>
            </div>
          </div>

          {inventoryItemIds.length > 0 && (
            <div className="mt-3">
              <h3>Matching Inventory Items:</h3>
              <InventoryList inventoryIds={inventoryItemIds} />
            </div>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default AIAssistantScreen;
